import { NO, UIRectangle, UIRoute, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementOffer, RHProcurementPartOffer, RHProcurementPublic } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHMakeOfferViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import type { RHOfferCapabilities } from "../../SocketClientFunctions"
import { ProcurementDetailsViewController } from "../ProcurementDetailsViewController"
import { ConfirmProcurementOfferView } from "./ConfirmProcurementOfferView"
import { EnterProcurementOfferView } from "./EnterProcurementOfferView"
import { OfferResultView } from "./OfferResultView"
import { OfferSubmittedView } from "./OfferSubmittedView"
import { OfferTeamView } from "./OfferTeamView"
import { TendererParticipationView } from "./TendererParticipationView"


export interface RHMakeOfferLoadResult {
    offerAccessDate: string | null
    renderedClosedWithoutContractPartIds: string[]
}


/**
 * Wrapper view that owns the offer flow for the offerer role.
 *
 * Before the deadline (three-step active offer flow):
 *   Step 1 — EnterProcurementOfferView   (enter / edit offer data)
 *   Step 2 — ConfirmProcurementOfferView (read-only preview + submit)
 *   Step 3 — OfferSubmittedView          (success screen + edit / rescind)
 *
 * After the deadline:
 *   Step 4 — OfferResultView             (read-only offer + evaluation result)
 *
 * Above the offer flow the procurement's general details are shown directly.
 *
 * On load, the furthest applicable step is shown automatically:
 *   - Deadline has passed + submitted offer  → Step 4
 *   - Submitted offer exists (pre-deadline)  → Step 3
 *   - Draft offer exists                     → Step 2 (confirm view pre-populated)
 *   - Neither                                → Step 1
 */
export class MakeOfferView extends UIView {
    
    
    // ── Offer flow steps ──────────────────────────────────────────────────────
    readonly enterView = new EnterProcurementOfferView()
    readonly confirmView = new ConfirmProcurementOfferView()
    readonly submittedView = new OfferSubmittedView()
    readonly resultView = new OfferResultView()
    readonly closedOffersView = new OfferResultView()
    readonly newOfferButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Koosta uus pakkumus" },
        variant: RHMakeOfferViewTokens.confirmButtonVariant,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => { void this._showNewOfferForm() }
        }
    })
    readonly offerTeamView = new OfferTeamView()
    readonly tendererParticipationView = new TendererParticipationView()
    readonly restrictedOfferBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Warning
    })
    readonly resubmissionBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Warning,
        textAlignment: UITextView.textAlignment.center
    })
    readonly noOfferLabel = new UITextView().configuredWithObject({
        text: "Puuduvad",
        textAlignment: UITextView.textAlignment.center
    })
    readonly offerDeadlineBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Warning,
        text: "Pakkumuste esitamise tähtaeg on möödas.",
        textAlignment: UITextView.textAlignment.center
    })
    
    readonly deleteDraftOfferButton = this.confirmView.deleteDraftOfferButton.configuredWithObject({
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => this._confirmDraftOfferDeletion()
        }
    }).addedAsSubviewToView(this)

    /** Called after the offer has been successfully submitted. */
    didSubmitProcurementOffer?: (submittedOffer: RHProcurementOffer) => void

    /** Called after an in-progress offer draft has been saved from the entry step. */
    didSaveDraftAndNavigateToDashboard?: () => void | Promise<void>
    
    /** Called whenever the server-side offer state changes enough to rename the parent tab. */
    offerStateDidChange?: (
        state: "empty" | "draft" | "submitted",
        offer?: RHProcurementOffer,
        capabilities?: RHOfferCapabilities
    ) => void

    /** Set to the result of the most recent successful load. Cleared at the start of each load. */
    lastLoadResult: RHMakeOfferLoadResult | undefined

    /** Called when a load completes successfully. Not called for superseded loads. */
    offerContentDidLoad?: (result: RHMakeOfferLoadResult) => void
    
    override _focusManager = new RHFocusManager(() => {
        const focusableItems: (UIView | RHFocusManager)[] = []
        const activeStepView = this.activeStepView
        if (activeStepView.superview === this) {
            const activeManager = activeStepView._focusManager
            if (activeManager) {
                focusableItems.push(activeManager)
            }
        }
        if (this.deleteDraftOfferButton.superview === this && !this.deleteDraftOfferButton.hidden) {
            focusableItems.push(this.deleteDraftOfferButton)
        }
        if (this.closedOffersView.superview === this) {
            focusableItems.push(this.closedOffersView._focusManager)
        }
        if (this.newOfferButton.superview === this) {
            focusableItems.push(this.newOfferButton)
        }
        return focusableItems as UIView[]
    })
    
    _procurement?: RHProcurementPublic
    isReadOnly = NO
    hasNextRound = NO
    displayedPartId?: string
    _rootPartCount = 0
    teamContentView?: OfferTeamView | TendererParticipationView
    _draftOfferId?: string
    _closedOffers: RHProcurementOffer[] = []
    _partIdsRequiringResubmission: string[] = []
    _contentLoadGeneration = 0

    get isOfferResubmissionRequired(): boolean {
        if (!this.offerActionsEnabled || this.isReadOnly || !this._procurement) {
            return NO
        }
        const closedPartIds = [
            ...(this._procurement.finalizedPartIds ?? []),
            ...(this._procurement.closedWithoutContractPartIds ?? [])
        ].map(partId => partId.toString())
        return this._procurement.parts.some(part =>
            this._partIdsRequiringResubmission.includes(part._id.toString()) &&
            !closedPartIds.includes(part._id.toString())
        )
    }
    
    get offerActionsEnabled(): boolean {
        return this.enterView.offerActionsEnabled
    }
    
    set offerActionsEnabled(enabled: boolean) {
        this.enterView.offerActionsEnabled = enabled
        this.confirmView.areOfferActionsEnabled = enabled
    }
    
    constructor(elementID?: string) {
        super(elementID)

        this.enterView.deleteDraftOfferButton = this.deleteDraftOfferButton
        
        // ── Step 1 → Step 2 ───────────────────────────────────────────────────
        this.enterView.didSaveDraftAndProceedToConfirm = (
            procurement,
            savedDraftOffer,
            offersByPartId
        ) => this._showSavedDraftOffer(procurement, savedDraftOffer, offersByPartId)
        this.enterView.didSaveDraftAndNavigateToDashboard = () => {
            return this.didSaveDraftAndNavigateToDashboard?.()
        }
        
        // ── Step 2: back → Step 1 ─────────────────────────────────────────────
        this.confirmView.backButton.addTargetForControlEvent(
            RHButton.controlEvent.PointerUpInside,
            () => {
                this._goBackToEnter()
            }
        )
        
        // ── Step 2 → Step 3 ───────────────────────────────────────────────────
        this.confirmView.didSubmitProcurementOffer = (submittedOffer) => {
            this._draftOfferId = undefined
            this.deleteDraftOfferButton.hidden = YES
            let capabilities: RHOfferCapabilities | undefined
            if (this._procurement) {
                capabilities = this._offerCapabilitiesForOfferInProcurement(
                    submittedOffer,
                    this._procurement._id + ""
                )
                this._applyOfferCapabilitiesToStepViews(capabilities)
                this.submittedView.configure(this._procurement, submittedOffer, this._closedOffers)
                this._showSubmitted()
            }
            this.offerStateDidChange?.("submitted", submittedOffer, capabilities)
            this.didSubmitProcurementOffer?.(submittedOffer)
        }
        
        // ── Step 3 Rescind → details tab ─────────────────────────────────────
        this.submittedView.didRescindOffer = () => {
            this.offerStateDidChange?.("empty")
            UIRoute.currentRoute
                .routeBySettingParameterInComponent(
                    ProcurementDetailsViewController.routeComponentName,
                    ProcurementDetailsViewController.ParameterIdentifierName.tab,
                    "details"
                )
                .apply()
        }
        
        this.submittedView.didUpdateOffer = (updatedOffer) => {
            const capabilities = this._offerCapabilitiesForOfferInProcurement(
                updatedOffer,
                this._procurement?._id?.toString() ?? ""
            )
            this.offerStateDidChange?.("submitted", updatedOffer, capabilities)
            void this._showOfferTeamForOffer(updatedOffer)
        }

        this.offerTeamView.teamDidChange = async capabilities => {
            this.deleteDraftOfferButton.hidden = !this._draftOfferId || this.isReadOnly ||
                capabilities?.canEdit !== YES
            this._applyOfferCapabilitiesToStepViews(capabilities)
            if (
                capabilities?.canViewDetailed === YES &&
                this.activeStepView.superview !== this &&
                this._procurement
            ) {
                try {
                    await this.loadForProcurement(this._procurement, this._rootPartCount)
                }
                catch (error) {
                    console.error("Offer reload after team change failed:", error)
                    this.restrictedOfferBanner.configureBannerWithObject({
                        alertType: RHAlertBannerView.type.Error,
                        text: "Pakkumuse andmete laadimine ebaõnnestus. Palun proovi uuesti."
                    })
                    this.restrictedOfferBanner.addedAsSubviewToView(this)
                    this.setNeedsLayoutUpToRootView()
                }
                return
            }
            this.setNeedsLayoutUpToRootView()
        }

        this.tendererParticipationView.participationDidChange = () => {
            this.setNeedsLayoutUpToRootView()
        }
    }
    
    // ── Public API ────────────────────────────────────────────────────────────
    
    async loadForProcurement(
        procurement: RHProcurementPublic,
        numberOfRootParts: number
    ): Promise<boolean> {
        const loadGeneration = ++this._contentLoadGeneration
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const isCurrentLoad = () => loadGeneration === this._contentLoadGeneration &&
            companyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        this.enterView.cancelPendingLoad()
        this.enterView.cancelPendingSaveIfInvalidForProcurement(procurement)
        this._procurement = procurement
        this._rootPartCount = numberOfRootParts
        this._draftOfferId = undefined
        this.lastLoadResult = undefined
        this._closedOffers = []
        this.newOfferButton.removeFromSuperview()
        this.closedOffersView.removeFromSuperview()
        this.noOfferLabel.removeFromSuperview()
        this.offerDeadlineBanner.removeFromSuperview()
        this.deleteDraftOfferButton.hidden = YES
        const isReadOnly = this.isReadOnly || procurement.status !== "published"
        this.offerActionsEnabled = !isReadOnly && !this._hasOfferSubmissionDeadlinePassedForProcurement(procurement)
        this.resultView.configureWithObject({
            displayedPartId: this.displayedPartId,
            hasNextRound: this.hasNextRound,
            isHistorical: this.isReadOnly,
            isDraft: NO
        })
        
        const procurementId = procurement._id + ""
        const hasDeadlinePassed = this._hasOfferSubmissionDeadlinePassedForProcurement(procurement)
        
        // Check submitted offer first — it takes priority over any draft.
        const submittedOfferAccess = await RHCore.sharedInstance.offerStore
            .retrieveSubmittedOfferAccessSnapshotForProcurement(procurementId)
        if (!isCurrentLoad()) {
            return NO
        }
        this._updateResubmissionRequirementForPartIds(submittedOfferAccess.partIdsRequiringResubmission)
        const submittedOffer = submittedOfferAccess.offer
        this._closedOffers = submittedOfferAccess.closedOffers ?? []
        if (submittedOffer?.status === "rescinded") {
            this._closedOffers = [submittedOffer, ...this._closedOffers]
        }
        const offerAccessDate = submittedOfferAccess.notificationSnapshotAt
        const completeLoad = (renderedClosedWithoutContractPartIds: string[]) => {
            this.lastLoadResult = { offerAccessDate, renderedClosedWithoutContractPartIds }
            this.offerContentDidLoad?.(this.lastLoadResult)
            return YES
        }
        const closedWithoutContractPartIds = (procurement.closedWithoutContractPartIds ?? [])
            .map(partId => partId.toString())
            .filter(partId => !this.displayedPartId || partId === this.displayedPartId)
        
        if (submittedOffer && submittedOffer.status !== "rescinded") {
            this.enterView.discardSessionSnapshotForProcurement(procurementId)
            const capabilities = this._offerCapabilitiesForOfferInProcurement(submittedOffer, procurementId)
            this.offerStateDidChange?.("submitted", submittedOffer, capabilities)
            await this._showOfferTeamForOffer(submittedOffer)
            if (!isCurrentLoad()) {
                return NO
            }
            this._applyOfferCapabilitiesToStepViews(capabilities)
            if (hasDeadlinePassed || isReadOnly) {
                // Post-deadline: show read-only result view with evaluation status
                this.resultView.configure(procurement, submittedOffer, numberOfRootParts, this._closedOffers)
                this._showResult()
                return completeLoad(closedWithoutContractPartIds)
            }
            // Pre-deadline: show the editable submitted view
            this.submittedView.configure(procurement, submittedOffer, this._closedOffers)
            this._showSubmitted()
            return completeLoad(closedWithoutContractPartIds)
        }
        this._showClosedOffersForProcurement(procurement)
        const renderedClosedWithoutContractPartIds = closedWithoutContractPartIds.filter(partId =>
            this._closedOffers.some(offer =>
                (offer.partOffers as RHProcurementPartOffer[]).some(partOffer => partOffer.partId.toString() === partId)
            )
        )
        if (!submittedOffer && submittedOfferAccess.offerId) {
            this.enterView.discardSessionSnapshotForProcurement(procurementId)
            this.offerStateDidChange?.(
                "submitted",
                undefined,
                submittedOfferAccess.capabilities
            )
            await this._showRestrictedOfferAccessForOfferId(
                submittedOfferAccess.offerId,
                submittedOfferAccess.capabilities
            )
            if (!isCurrentLoad()) {
                return NO
            }
            return completeLoad(renderedClosedWithoutContractPartIds)
        }

        if (procurement.status !== "closed-without-contract") {
            // No submitted offer — check for a draft and land on confirm if found.
            const draftOfferAccess = await RHCore.sharedInstance.offerStore
                .retrieveDraftOfferAccessSnapshotForProcurement(procurementId)
            if (!isCurrentLoad()) {
                return NO
            }
            const draftOffer = draftOfferAccess.offer

            if (draftOffer) {
                this.enterView.discardSessionSnapshotForProcurement(procurementId)
                const capabilities = this._offerCapabilitiesForOfferInProcurement(draftOffer, procurementId)
                this.offerStateDidChange?.("draft", draftOffer, capabilities)
                await this._showOfferTeamForOffer(draftOffer)
                if (!isCurrentLoad()) {
                    return NO
                }
                this._draftOfferId = draftOffer._id?.toString()
                this.deleteDraftOfferButton.hidden = this.isReadOnly || capabilities?.canEdit !== YES
                if (isReadOnly || hasDeadlinePassed) {
                    this.resultView.isDraft = YES
                    this.resultView.configure(procurement, draftOffer, numberOfRootParts)
                    this._showResult()
                    return completeLoad(renderedClosedWithoutContractPartIds)
                }
                const partOffers = draftOffer.partOffers as RHProcurementPartOffer[]
                const offersByPartId = new Map<string, RHProcurementPartOffer[]>()
                for (const partOffer of partOffers) {
                    const key = partOffer.partId.toString()
                    if (!offersByPartId.has(key)) {
                        offersByPartId.set(key, [])
                    }
                    offersByPartId.get(key)!.push(partOffer)
                }
                this.confirmView.configure(procurement, draftOffer, offersByPartId)
                this.confirmView.offerCapabilities = capabilities
                this._showConfirm()
                return completeLoad(renderedClosedWithoutContractPartIds)
            }
            if (draftOfferAccess.offerId) {
                this.enterView.discardSessionSnapshotForProcurement(procurementId)
                this.offerStateDidChange?.("draft", undefined, draftOfferAccess.capabilities)
                await this._showRestrictedOfferAccessForOfferId(
                    draftOfferAccess.offerId,
                    draftOfferAccess.capabilities
                )
                if (!isCurrentLoad()) {
                    return NO
                }
                return completeLoad(renderedClosedWithoutContractPartIds)
            }
        }
        if (this._closedOffers.length > 0) {
            const closedOffer = this._closedOffers[0]
            this.offerStateDidChange?.("submitted", closedOffer, submittedOfferAccess.capabilities)
            this._hideOfferTeam()
            this.closedOffersView.removeFromSuperview()
            this.resultView.configure(procurement, closedOffer, numberOfRootParts, this._closedOffers.slice(1))
            this._showResult()
            if (this.offerActionsEnabled && submittedOfferAccess.capabilities?.canSubmitOffer === YES &&
                procurement.parts.some(part =>
                    (!this.displayedPartId || part._id.toString() === this.displayedPartId) &&
                    !(procurement.finalizedPartIds ?? []).includes(part._id.toString()) &&
                    !closedWithoutContractPartIds.includes(part._id.toString())
                )) {
                this.newOfferButton.addedAsSubviewToView(this)
            }
            return completeLoad(renderedClosedWithoutContractPartIds)
        }

        // Nothing on server — start from scratch.
        this.offerStateDidChange?.("empty")
        this._hideOfferTeam()
        if (isReadOnly || hasDeadlinePassed) {
            if (isReadOnly || closedWithoutContractPartIds.length > 0) {
                this.resultView.configure(procurement, null, numberOfRootParts)
                this._showResult()
                return completeLoad(closedWithoutContractPartIds)
            }
            this.enterView.removeFromSuperview()
            this.confirmView.removeFromSuperview()
            this.submittedView.removeFromSuperview()
            this.resultView.removeFromSuperview()
            this.restrictedOfferBanner.removeFromSuperview()
            if (hasDeadlinePassed) {
                this.offerDeadlineBanner.addedAsSubviewToView(this)
            }
            else {
                this.noOfferLabel.addedAsSubviewToView(this)
            }
            this.setNeedsLayoutUpToRootView()
            return completeLoad([])
        }
        await this._showTendererParticipationForProcurement(procurementId)
        if (!isCurrentLoad()) {
            return NO
        }
        this._showEnter()
        await this.enterView.loadForProcurement(procurement)
        if (!isCurrentLoad()) {
            return NO
        }
        return completeLoad(closedWithoutContractPartIds)
    }


    _updateResubmissionRequirementForPartIds(partIds: string[]) {
        this._partIdsRequiringResubmission = partIds.filter(partId =>
            !this.displayedPartId || partId === this.displayedPartId
        )
        this.resubmissionBanner.removeFromSuperview()
        const affectedParts = this._procurement?.parts.filter(part =>
            this._partIdsRequiringResubmission.includes(part._id.toString())
        ) ?? []
        if (affectedParts.length > 0) {
            let subject = "Teie pakkumus"
            if ((this._procurement?.parts.length ?? 0) > 1) {
                subject = "Teie pakkumus hanke osale "
                if (affectedParts.length > 1) {
                    subject = "Teie pakkumus hanke osadele "
                }
                subject += affectedParts.map(part => part.partNumber).join(", ")
            }
            let text = subject + " ei ole enam kehtiv, kuna see ei sisalda Hankija poolt nõutud kohustuslikke dokumente."
            if (this.isOfferResubmissionRequired) {
                text += " Palun esita enda pakkumus uuesti"
            }
            this.resubmissionBanner.configureBannerWithObject({ text })
            this.resubmissionBanner.addedAsSubviewToView(this)
        }
        this.setNeedsLayoutUpToRootView()
    }


    _reloadOfferContent() {
        if (!this._procurement || !this.isMemberOfViewTree) {
            return
        }
        void this.loadForProcurement(this._procurement, this._rootPartCount).catch(error => {
            console.error("Offer content reload failed:", error)
        })
    }

    updateRequiredDocumentsForProcurement(procurement: RHProcurementPublic) {
        if (this.enterView.updateRequiredDocumentsForProcurement(procurement).hasRequiredDocumentChanges) {
            this._contentLoadGeneration += 1
            this._procurement = procurement
        }
    }


    _showClosedOffersForProcurement(procurement: RHProcurementPublic) {
        const closedOffer = this._closedOffers[0]
        if (!closedOffer) {
            this.closedOffersView.removeFromSuperview()
            return
        }
        this.closedOffersView.configureWithObject({
            displayedPartId: this.displayedPartId,
            isHistorical: this.isReadOnly,
            hasNextRound: this.hasNextRound,
            shouldShowOnlyOfferedParts: YES
        })
        this.closedOffersView.configure(procurement, closedOffer, this._rootPartCount, this._closedOffers.slice(1))
        this.closedOffersView.addedAsSubviewToView(this)
    }


    async _showNewOfferForm() {
        const procurement = this._procurement
        const loadGeneration = this._contentLoadGeneration
        if (!procurement || !this.offerActionsEnabled || this.newOfferButton.superview !== this) {
            return
        }
        this.newOfferButton.removeFromSuperview()
        this._showClosedOffersForProcurement(procurement)
        this._showEnter()
        await this._showTendererParticipationForProcurement(procurement._id + "")
        if (loadGeneration !== this._contentLoadGeneration) {
            return
        }
        await this.enterView.loadForProcurement(procurement)
        this.setNeedsLayoutUpToRootView()
    }


    _confirmDraftOfferDeletion() {
        const offerId = this._draftOfferId
        const procurementId = this._procurement?._id + ""
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (!offerId || this.deleteDraftOfferButton.hidden || this.deleteDraftOfferButton.loading) {
            return
        }
        const shower = CBDialogViewShower.showQuestionDialog("Kas soovid pakkumuse mustandi kustutada?")
        shower.dialogView.view.configureWithObject({
            yesButton: { titleLabel: { text: "Kustuta mustand" } },
            noButton: { titleLabel: { text: "Tühista" } }
        })
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            if (!this.isMemberOfViewTree || this._draftOfferId !== offerId ||
                this.deleteDraftOfferButton.hidden || this.deleteDraftOfferButton.loading ||
                companyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId) {
                return
            }
            this.deleteDraftOfferButton.loading = YES
            try {
                await RHCore.sharedInstance.offerStore.deleteDraftOfferWithId(offerId, procurementId)
                if (this.isMemberOfViewTree && this._draftOfferId === offerId &&
                    companyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId) {
                    this.discardUnsavedChanges()
                    this._reloadOfferContent()
                }
            }
            catch (error) {
                let message = "Pakkumuse mustandi kustutamine ebaõnnestus."
                if (error instanceof Error && error.message) {
                    message = error.message
                }
                CBDialogViewShower.alert(message)
            }
            finally {
                this.deleteDraftOfferButton.loading = NO
            }
        }
    }


    discardUnsavedChanges() {
        this._contentLoadGeneration += 1
        this.enterView.discardUnsavedChanges()
    }


    get hasUnsavedOfferData(): boolean {
        return this.activeStepView === this.enterView &&
            this.enterView.hasSections &&
            this.enterView.hasUnsavedSessionData
    }


    async saveDraftAndProceedToConfirmation(): Promise<boolean> {
        if (!this.hasUnsavedOfferData) {
            return YES
        }
        return await this.enterView.saveDraftAndProceedToConfirmation()
    }


    async saveDraft(): Promise<boolean> {
        if (!this.hasUnsavedOfferData) {
            return YES
        }
        return await this.enterView.saveDraft()
    }
    
    
    _hasOfferSubmissionDeadlinePassedForProcurement(procurement: RHProcurementPublic): boolean {
        if (!procurement.deadlineDate) {
            return NO
        }
        return new Date(procurement.deadlineDate) <= new Date()
    }


    async _showSavedDraftOffer(
        procurement: RHProcurementPublic,
        savedDraftOffer: RHProcurementOffer,
        offersByPartId: Map<string, RHProcurementPartOffer[]>
    ) {
        const loadGeneration = this._contentLoadGeneration
        const procurementId = procurement._id + ""
        const capabilities = this._offerCapabilitiesForOfferInProcurement(savedDraftOffer, procurementId)

        this.offerStateDidChange?.("draft", savedDraftOffer, capabilities)
        this.confirmView.configure(procurement, savedDraftOffer, offersByPartId)
        this.confirmView.offerCapabilities = capabilities

        await this._showOfferTeamForOffer(savedDraftOffer)
        if (loadGeneration !== this._contentLoadGeneration) {
            return
        }
        this._draftOfferId = savedDraftOffer._id?.toString()
        this.deleteDraftOfferButton.hidden = this.isReadOnly || capabilities?.canEdit !== YES
        this._showConfirm()
    }


    _offerCapabilitiesForOfferInProcurement(offer: RHProcurementOffer, procurementId: string) {
        const offerId = (offer._id ?? "") + ""
        let capabilities = RHCore.sharedInstance.offerStore.offerCapabilitiesForOfferId(offerId)
        if (!capabilities) {
            capabilities = RHCore.sharedInstance.offerStore.offerCapabilitiesForProcurementId(procurementId)
        }
        return capabilities
    }


    _applyOfferCapabilitiesToStepViews(capabilities: RHOfferCapabilities | undefined) {
        this.confirmView.offerCapabilities = capabilities
        this.submittedView.offerCapabilities = capabilities
    }


    async _showOfferTeamForOffer(offer: RHProcurementOffer) {
        const offerId = this._offerIdFromOffer(offer)
        if (offerId.length === 0) {
            this._hideOfferTeam()
            return
        }

        await this._showOfferTeamForOfferId(offerId)
    }


    async _showOfferTeamForOfferId(offerId: string) {
        if (offerId.length === 0 || this._procurement?.status === "closed-without-contract") {
            this._hideOfferTeam()
            return
        }

        this._hideTendererParticipation()
        this.teamContentView = this.offerTeamView
        await this.offerTeamView.load(offerId, this._procurement?._id?.toString())
        if (this.offerTeamView.capabilities) {
            this._applyOfferCapabilitiesToStepViews(this.offerTeamView.capabilities)
        }
        this.setNeedsLayoutUpToRootView()
    }


    async _showRestrictedOfferAccessForOfferId(
        offerId: string,
        capabilities: RHOfferCapabilities | undefined
    ) {
        this.enterView.removeFromSuperview()
        this.confirmView.removeFromSuperview()
        this.submittedView.removeFromSuperview()
        this.resultView.removeFromSuperview()
        this._hideTendererParticipation()
        this._applyOfferCapabilitiesToStepViews(capabilities)

        if (
            capabilities?.role === "administrator" &&
            capabilities.canAddSelfToTeam === YES
        ) {
            this.restrictedOfferBanner.removeFromSuperview()
            this.setNeedsLayoutUpToRootView()
            await this._showOfferTeamForOfferId(offerId)
            return
        }
        this.restrictedOfferBanner.configureBannerWithObject({
            alertType: RHAlertBannerView.type.Warning,
            text: "Sellel ettevõttel on selle hanke jaoks pakkumus olemas, kuid sul puudub selle pakkuja meeskonnaroll. Pakkumuse sisu ei kuvata."
        })
        this.restrictedOfferBanner.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
        this._hideOfferTeam()
    }


    _hideOfferTeam() {
        this.offerTeamView.removeFromSuperview()
        if (this.teamContentView === this.offerTeamView) {
            this.teamContentView = undefined
        }
    }


    async _showTendererParticipationForProcurement(procurementId: string) {
        if (procurementId.length === 0) {
            this._hideTendererParticipation()
            return
        }

        this._hideOfferTeam()
        this.teamContentView = this.tendererParticipationView
        await this.tendererParticipationView.load(procurementId)
        this.setNeedsLayoutUpToRootView()
    }


    _hideTendererParticipation() {
        this.tendererParticipationView.removeFromSuperview()
        if (this.teamContentView === this.tendererParticipationView) {
            this.teamContentView = undefined
        }
    }


    _offerIdFromOffer(offer: RHProcurementOffer): string {
        const rawOfferId = offer._id
        if (!rawOfferId) {
            return ""
        }
        return rawOfferId + ""
    }
    // ── Private helpers ───────────────────────────────────────────────────────
    
    _showEnter() {
        this.restrictedOfferBanner.removeFromSuperview()
        this.confirmView.removeFromSuperview()
        this.submittedView.removeFromSuperview()
        this.resultView.removeFromSuperview()
        this.enterView.addedAsSubviewToView(this)
        this.deleteDraftOfferButton.removeFromSuperview()
        this.deleteDraftOfferButton.addedAsSubviewToView(this.enterView)
    }
    
    _showConfirm() {
        this.restrictedOfferBanner.removeFromSuperview()
        this.enterView.removeFromSuperview()
        this.submittedView.removeFromSuperview()
        this.resultView.removeFromSuperview()
        this.confirmView.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
    }
    
    _showSubmitted() {
        this.closedOffersView.removeFromSuperview()
        this.restrictedOfferBanner.removeFromSuperview()
        this.enterView.removeFromSuperview()
        this.confirmView.removeFromSuperview()
        this.resultView.removeFromSuperview()
        this.submittedView.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
    }
    
    _showResult() {
        this.restrictedOfferBanner.removeFromSuperview()
        this.enterView.removeFromSuperview()
        this.confirmView.removeFromSuperview()
        this.submittedView.removeFromSuperview()
        this.resultView.addedAsSubviewToView(this)
        this.deleteDraftOfferButton.removeFromSuperview()
        this.deleteDraftOfferButton.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
    }
    
    _goBackToEnter() {
        this._showEnter()
        if (this.enterView.hasSections && !this.confirmView._hasOffersForFinalizedParts) {
            this.enterView.restoreForEditing()
        }
        else if (this._procurement) {
            void this.enterView.loadForProcurement(this._procurement)
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    get activeStepView(): UIView {
        if (this.offerDeadlineBanner.superview === this) {
            return this.offerDeadlineBanner
        }
        else if (this.noOfferLabel.superview == this) {
            return this.noOfferLabel
        }
        else if (this.restrictedOfferBanner.superview == this) {
            return this.restrictedOfferBanner
        }
        else if (this.enterView.superview == this) {
            return this.enterView
        }
        else if (this.confirmView.superview == this) {
            return this.confirmView
        }
        else if (this.resultView.superview == this) {
            return this.resultView
        }
        else {
            return this.submittedView
        }
    }
    
    override set frame(frame: UIRectangle) {
        super.frame = frame
    }
    
    override get frame() {
        return super.frame
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)

        const flowViews = this._visibleFlowViews()
        if (flowViews.length === 0) {
            return
        }
        const flowViewHeights = flowViews.map(view => {
            if (view === this.deleteDraftOfferButton || view === this.newOfferButton) {
                return RHMakeOfferViewTokens.actionButtonHeight
            }
            return view
        })
        if (flowViews.length > 1) {
            bounds.framesByDistributingViewsAsColumn(flowViews, padding, flowViewHeights)
        }
        else {
            bounds
                .rectangleWithHeight(flowViewHeights[0])
                .assignedAsFrameOfView(flowViews[0])
        }

        if (this.deleteDraftOfferButton.superview === this && !this.deleteDraftOfferButton.hidden) {
            this.deleteDraftOfferButton.frame
                .rectangleWithMaxWidth(RHMakeOfferViewTokens.actionButtonMinWidth, 0.5)
                .assignedAsFrameOfView(this.deleteDraftOfferButton)
        }
        this.newOfferButton.frame
            .IF(this.newOfferButton.superview === this)
                .rectangleWithMaxWidth(RHMakeOfferViewTokens.actionButtonMaxWidth, 0.5)
                .assignedAsFrameOfView(this.newOfferButton)
            .ENDIF()
    }


    override intrinsicContentHeight(constrainingWidth = 0) {
        const padding = this.core.paddingLength
        const flowViews = this._visibleFlowViews()
        let height = 0

        for (const view of flowViews) {
            if (view === this.deleteDraftOfferButton || view === this.newOfferButton) {
                height += RHMakeOfferViewTokens.actionButtonHeight
            }
            else {
                height += view.intrinsicContentHeight(constrainingWidth)
            }
        }
        if (flowViews.length > 1) {
            height += padding * (flowViews.length - 1)
        }
        return height
    }


    _visibleFlowViews(): UIView[] {
        const flowViews: UIView[] = []
        if (this.resubmissionBanner.superview === this) {
            flowViews.push(this.resubmissionBanner)
        }
        if (this.activeStepView.superview === this) {
            flowViews.push(this.activeStepView)
        }
        if (this.deleteDraftOfferButton.superview === this && !this.deleteDraftOfferButton.hidden) {
            flowViews.push(this.deleteDraftOfferButton)
        }
        if (this.closedOffersView.superview === this) {
            flowViews.push(this.closedOffersView)
        }
        if (this.newOfferButton.superview === this) {
            flowViews.push(this.newOfferButton)
        }
        return flowViews
    }
    
}
